import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { login as apiLogin, signup as apiSignup, logout as apiLogout, getMe, submitChallenge } from '@/api/auth';
import type { ChallengeResponse } from '@/api/auth';
import type { User } from '@/types';
import { queryClient } from '@/lib/query-client';
import { resetClientState } from '@/lib/client-reset';
import { useMailStore } from '@/stores/mail';

// Drop the previous session's cached mail, mailbox selection and pop-out draft handoffs (B-06).
const forgetPreviousUser = () => {
  resetClientState(queryClient, { resetMail: () => useMailStore.getState().reset() });
  try {
    Object.keys(localStorage).filter((k) => k.startsWith('bmail-popout-handoff:')).forEach((k) => localStorage.removeItem(k));
  } catch { /* storage blocked */ }
};

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  needsCredentials: Array<{ id: string; email: string }>;
  login: (handle: string, password: string, keepSignedIn?: boolean) => Promise<ChallengeResponse | null>;
  completeChallenge: (challengeId: string, answers: Record<string, string>) => Promise<void>;
  signup: (handle: string, password: string, displayName?: string, email?: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  setUser: (u: User) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      error: null,
      needsCredentials: [],

      login: async (handle, password, keepSignedIn = false) => {
        set({ isLoading: true, error: null });
        try {
          const res = await apiLogin(handle, password, keepSignedIn);
          if ('challengeRequired' in res) {
            set({ isLoading: false });
            return res; // caller shows the security-questions step
          }
          forgetPreviousUser();
          set({ user: res.user, isLoading: false, needsCredentials: [] });
          return null;
        } catch (err: any) {
          set({ error: err.message || 'Login failed', isLoading: false });
          throw err;
        }
      },

      completeChallenge: async (challengeId, answers) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = await submitChallenge(challengeId, answers);
          forgetPreviousUser();
          set({ user, isLoading: false, needsCredentials: [] });
        } catch (err: any) {
          set({ error: err.message || 'Verification failed', isLoading: false });
          throw err;
        }
      },

      signup: async (handle, password, displayName, email) => {
        set({ isLoading: true, error: null });
        try {
          const { user } = await apiSignup(handle, password, displayName, email);
          forgetPreviousUser();
          set({ user, isLoading: false, needsCredentials: [] });
        } catch (err: any) {
          set({ error: err.message || 'Signup failed', isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        // Forget locally first: callers don't await this, and a slow POST must not finish after
        // the next user has signed in and wipe their state.
        forgetPreviousUser();
        set({ user: null, needsCredentials: [] });
        try {
          await apiLogout();
        } catch {}
      },

      loadUser: async () => {
        try {
          const { user, needsCredentials } = await getMe();
          set({ user, needsCredentials: needsCredentials || [] });
        } catch {
          set({ user: null, needsCredentials: [] });
        }
      },

      clearError: () => set({ error: null }),
      setUser: (u) => set({ user: u })
    }),
    {
      name: 'bmail-auth',
      partialize: (state) => ({ user: state.user })
    }
  )
);